import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";

import AutoComplete from "@/components/hookForms/autoComplete";
import messages from "@/utils/messages";
import { Title } from "@/components/title/style";
import IGenericResult from "@/interfaces/IGenericResult";
import TextInput from "@/components/hookForms/textField";
import { LoadingButton } from "@mui/lab";
import NewFileInputDrop from "@/components/newFileInputDrop";
import serviceModeloFatura from "@/services/serviceModeloFatura";
import { getBase64 } from "@/utils/utils";

const requiredFields = [
    { name: "concessionariaID", label: "Concessionária" },
    { name: "descricaoModelo", label: "Descrição Modelo" },
    { name: "tipoFatura", label: "Tipo de Fatura" },
];

export interface IDialogProps {
    open: boolean;
    onSuccess: () => void;
    onClose?: any;
    concessionariaDataOptions: IGenericResult[];
    tipoFaturaDataOptions: any;
    modeloDataOptions: any;
}
const Modal: React.FC<IDialogProps> = ({
    open,
    concessionariaDataOptions,
    tipoFaturaDataOptions,
    modeloDataOptions,
    onClose,
    onSuccess,
}) => {
    const [selectedFileBase64, setSelectedFileBase64] = useState<{ stringBase64: string; type: string }>({
        stringBase64: "",
        type: "",
    });
    const [isDisable, setDisable] = useState(false);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            descricaoModelo: "",
            concessionariaID: 0,
            modeloVinculado: "",
            tipoFatura: "",
        },
    });

    const clearFile = () => {
        setSelectedFileBase64({ stringBase64: "", type: "" });
    };

    const handleFileInputDrop = (selectedFiles: File[]) => {
        if (selectedFiles) {
            const size = selectedFiles[0].size / 1024 / 1024;

            if (size > 10) {
                toast.error(
                    "O tamanho do arquivo selecionado não pode ser maior do que 10MB. Arquivo '" +
                        selectedFiles[0].name +
                        "'."
                );
                clearFile();
            }

            getBase64(selectedFiles[0]).then((result) => {
                setSelectedFileBase64({ stringBase64: result, type: selectedFiles[0].type });
            });
        }
    };

    const onSubmit = (values: any) => {
        setDisable(true);

        const missingFields = requiredFields
            .filter((field) => {
                if (Array.isArray(values[field.name])) {
                    return values[field.name].length === 0;
                }
                return !values[field.name];
            })
            .map((field) => field.label);

        if (missingFields.length > 0) {
            toast.error(`Por favor, preencha os seguintes campos: ${missingFields.join(", ")}`);
            setDisable(false);
            return;
        }

        if (!selectedFileBase64.stringBase64) {
            toast.error("É necessário anexar o PDF ou XML da Fatura.");
            setDisable(false);
            return;
        }

        const fileType = selectedFileBase64.type.split("/")[1];

        if (fileType === "pdf" && values.modeloVinculado) {
            toast.error("Modelo Vinculado pode ser selecionado somente para XML!");
            setDisable(false);
            return;
        }

        const newValues = {
            ...values,
            arquivoFatura:
                fileType === "xml"
                    ? selectedFileBase64.stringBase64.replace("data:text/xml;base64,", "")
                    : selectedFileBase64.stringBase64,
            arquivoTipo: fileType,
            modeloVinculado: values.modeloVinculado.length === 0 ? null : values.modeloVinculado,
        };

        serviceModeloFatura
            .createModelo(newValues)
            .then((result) => {
                if (result) {
                    toast.success(messages.createSucess);
                    onClose();
                    onSuccess();
                    reset();
                }
            })
            .catch((err) => {
                toast.error(messages.createError, err);
            })
            .finally(() => {
                setDisable(false);
            });
    };

    return (
        <Box>
            <Dialog
                maxWidth="md"
                fullWidth
                onClose={(_, reason) => {
                    if (reason !== "backdropClick") {
                        clearFile();
                        reset();
                        onClose();
                    }
                }}
                open={open}
            >
                <DialogTitle>
                    <Title>Novo Cadastro de Modelo</Title>
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            reset();
                            onClose();
                        }}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent dividers>
                        <Grid container>
                            <Grid item md={12} xs={12}>
                                <AutoComplete
                                    id="concessionariaID"
                                    name="concessionariaID"
                                    label="Concessionária"
                                    options={concessionariaDataOptions}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <TextInput
                                    id="descricaoModelo"
                                    name="descricaoModelo"
                                    label="Descrição Modelo"
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                            <Grid item md={12} xs={12}>
                                <AutoComplete
                                    id="tipoFatura"
                                    name="tipoFatura"
                                    label="Tipo de Fatura"
                                    options={tipoFaturaDataOptions}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                            <Grid item md={12} xs={12}>
                                <AutoComplete
                                    id="modeloVinculado"
                                    name="modeloVinculado"
                                    label="Modelo Vinculado"
                                    placeholder="Somente para arquivos XML!"
                                    options={modeloDataOptions}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={12} mt={3}>
                                <NewFileInputDrop onDrop={handleFileInputDrop} />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions style={{ display: "flex", justifyContent: "center", margin: "24px" }}>
                        <Button
                            onClick={() => {
                                reset();
                                onClose();
                            }}
                            color="warning"
                            variant="contained"
                            style={{ width: "159px", height: "44px" }}
                        >
                            Cancelar
                        </Button>
                        <LoadingButton
                            type="submit"
                            color="primary"
                            variant="contained"
                            loading={isDisable}
                            disabled={isDisable}
                            style={{ width: "159px", height: "44px" }}
                        >
                            Salvar
                        </LoadingButton>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default Modal;
